import { useLazyQuery } from '@apollo/client';
import { Box, Button, ButtonSize, Heading, Icon, IconSize, ModalBody, Text } from '@bechtel/gds';
import { MetadataTableName, Note, NoteCommodityType, NoteType, Scalars } from '@bechtel/sr-types';
import { filter, forEach, get, map, replace } from 'lodash';
import { FC, useEffect, useMemo } from 'react';
import { AppSpinner, ErrorRetry, MultiLingualText } from '~/components';
import { GET_NOTES_BY_COMMODITY } from '~/gql';
import { useDate } from '~/hooks';
import { ModalHeaderStyled, ModalStyled, ViewNotesBoxStyled } from './styled';

export interface ViewChangeManagementNotesModalProps {
  id: Scalars['ID'];
  name: string;
  onClose: () => void;
}

const getNoteCommodityTypeByName = (name: string) => {
  if (name === MetadataTableName.CABLE) {
    return NoteCommodityType.Cable;
  } else if (name === MetadataTableName.RACEWAY) {
    return NoteCommodityType.Raceway;
  } else if (name === MetadataTableName.JUMPER) {
    return NoteCommodityType.Jumper;
  } else if (name === MetadataTableName.EQUIPMENT) {
    return NoteCommodityType.Equipment;
  } else if (name === MetadataTableName.EQUIPMENT_CODE) {
    return NoteCommodityType.EquipmentCode;
  } else if (name === MetadataTableName.RACEWAY_CODE) {
    return NoteCommodityType.RacewayCode;
  } else if (name === MetadataTableName.CABLE_CODE) {
    return NoteCommodityType.CableCode;
  } else if (name === MetadataTableName.CABLE_FROM_END) {
    return NoteCommodityType.CableFromTerm;
  } else if (name === MetadataTableName.CABLE_TO_END) {
    return NoteCommodityType.CableToTerm;
  }
  return null;
};
export const ViewChangeManagementNoteModal: FC<ViewChangeManagementNotesModalProps> = ({ id, name, onClose }) => {
  const { formatLocaleDate, formatLocaleTime } = useDate();

  const [getChangeManagementNotes, { loading, error, refetch, data }] = useLazyQuery<{
    notesByCommodity: Note[];
  }>(GET_NOTES_BY_COMMODITY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const items = useMemo(() => {
    const allNotes: Note[] = get(data, 'notesByCommodity') ?? [];
    const returnNotes = allNotes.length > 0 ? filter(allNotes, (allNote: Note) => allNote.noteType === NoteType.R) : [];
    if (!returnNotes || returnNotes.length === 0) return;
    let processedNotes = '';
    const notes = returnNotes[0]?.note?.split(/\r?\n\r?\n/);
    forEach(notes, (note) => {
      const dateIndex = note?.indexOf(',');
      const dateValue = note?.slice(0, dateIndex);
      if (!dateValue) return;
      const formattedNote = `${formatLocaleDate(dateValue)} ${formatLocaleTime(dateValue)}${note?.slice(dateIndex)}`;
      processedNotes = `${processedNotes}${formattedNote}\n\n`;
    });
    return [{ ...returnNotes, note: processedNotes }];
  }, [data, formatLocaleDate, formatLocaleTime]);

  // Get change management notes by commodity id
  useEffect(() => {
    if (!id) return;
    const noteCommodityType = getNoteCommodityTypeByName(name);
    if (!noteCommodityType) return;

    getChangeManagementNotes({
      variables: {
        commodityId: id,
        noteCommodityType: noteCommodityType,
      },
    });
  }, [getChangeManagementNotes, id, name]);

  return (
    <ModalStyled visible={true}>
      <ModalHeaderStyled title="">
        <Box pl={{ sm: '300' }}>
          <Heading size="500" weight="500">
            <MultiLingualText>Change Management Notes</MultiLingualText>
          </Heading>
        </Box>
        <Box css={{ marginLeft: 'auto ' }}>
          <Box alignItems={{ sm: 'center' }} p={{ sm: '400' }}>
            <Button size={ButtonSize.SMALL} css={{ background: 'transparent', boxShadow: 'none' }} onClick={onClose}>
              <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
            </Button>
          </Box>
        </Box>
      </ModalHeaderStyled>
      {/* @ts-ignore adjust in GDS component to take child as prop  */}
      <ModalBody
        css={`
          flex-direction: column;
        `}
      >
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }} pt={{ sm: '450' }} px={{ sm: '400' }}>
          <Box mb={{ sm: '300' }}>
            <MultiLingualText size="300" color="grey.900">
              Reason for the return
            </MultiLingualText>
          </Box>
          {loading && (
            <Box flexGrow={{ sm: 1 }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
              <AppSpinner size={IconSize.MEDIUM} />
            </Box>
          )}
          {error && <ErrorRetry refetch={refetch} />}
          {items && items.length > 0 && (
            <ViewNotesBoxStyled mt={{ sm: '100' }} mb={{ sm: '500' }} flexGrow={{ sm: 1 }}>
              {map(items, (item) => (
                <Box mb={{ sm: '100' }} p={{ sm: '100' }}>
                  <Text
                    size="400"
                    style={{ lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{
                      __html: item.note ? replace(item.note, /(?:[\n])/g, '<br/>') : '',
                    }}
                  ></Text>
                </Box>
              ))}
            </ViewNotesBoxStyled>
          )}
        </Box>
      </ModalBody>
    </ModalStyled>
  );
};
